import {
  AiBuilderChangeLabel,
  AiBuilderMessageKey,
  FlowChangeLine,
  TurnChanges,
} from '@activepieces/shared';
import { t } from 'i18next';
import {
  ChevronRight,
  CornerDownRight,
  Info,
  LucideIcon,
  Minus,
  Pencil,
  Plus,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import { ReactNode, useState } from 'react';

import { interpolateMissingIcuParams } from './ai-builder-utils';

import { badgeVariants } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const StepName = ({ children }: { children: string }) => (
  <span className="font-medium text-foreground">{children}</span>
);

/** The icon is the glance; the word beside it (`AiBuilderChangeLabel`) removes the doubt */
const lineIcon = (line: FlowChangeLine): LucideIcon => {
  switch (line.kind) {
    case 'step_added':
      return Plus;
    case 'step_removed':
    case 'values_removed':
      return Minus;
    case 'step_moved':
      return CornerDownRight;
    case 'trigger_set':
      return Zap;
    case 'step_renamed':
    case 'step_changed':
    case 'fields_updated':
      return Pencil;
  }
};

const lineContent = (line: FlowChangeLine): ReactNode => {
  switch (line.kind) {
    case 'step_added':
    case 'step_removed':
    case 'step_moved':
    case 'trigger_set':
    case 'step_changed':
      return <StepName>{line.step}</StepName>;
    case 'step_renamed':
      return (
        <>
          <StepName>{line.before}</StepName> → <StepName>{line.after}</StepName>
        </>
      );
    case 'fields_updated':
      return (
        <>
          <StepName>{line.step}</StepName> · {line.fields.join(', ')}
        </>
      );
    case 'values_removed':
      return (
        <>
          <StepName>{line.step}</StepName> · {line.field} :{' '}
          {line.values.join(', ')}
        </>
      );
  }
};

const translated = (key: string, params: Record<string, string> = {}) =>
  interpolateMissingIcuParams(t(key, params), params);

/**
 * What the turn changed, as the code read it from the saved flow:
 * folded under the answer, which already tells it in words. Shown open only
 * when nothing changed — that is when the answer may claim otherwise.
 */
export const TurnChangesSummary = ({ changes }: { changes: TurnChanges }) => {
  const [open, setOpen] = useState(false);

  if (changes.kind === 'nothing_changed') {
    return (
      <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
        <TriangleAlert className="w-3.5 h-3.5 shrink-0" aria-hidden />
        {translated(AiBuilderMessageKey.nothingChanged)}
      </div>
    );
  }

  return (
    <div className="mt-3 flex flex-col gap-1.5 text-xs text-muted-foreground">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex items-center gap-1.5 w-fit hover:text-foreground transition-colors"
      >
        <Pencil className="w-3.5 h-3.5 shrink-0" aria-hidden />
        {translated(AiBuilderMessageKey.changesHeading)}
        <span
          className={cn(
            badgeVariants({ variant: 'secondary' }),
            'px-1.5 py-0 text-[10px] leading-4',
          )}
        >
          {changes.lines.length}
        </span>
        <ChevronRight
          className={`w-3.5 h-3.5 transition-transform ${
            open ? 'rotate-90' : ''
          }`}
          aria-hidden
        />
      </button>
      {open && (
        <ul className="ml-5 flex flex-col gap-1">
          {changes.lines.map((line, index) => {
            const Icon = lineIcon(line);
            return (
              <li key={index} className="flex items-start gap-1.5">
                <Icon className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden />
                <span>
                  {lineContent(line)}
                  {' · '}
                  {translated(AiBuilderChangeLabel[line.kind])}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {changes.republishNeeded && (
        <div className="flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden />
          {translated(AiBuilderMessageKey.republishReminder)}
        </div>
      )}
    </div>
  );
};
